import { range, clamp } from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import Svg from 'rp-react-svg-icon';

/**
 * Pagination presentational component
 */
export default class Pagination extends PureComponent {
    static propTypes = {
        /**
         * Total number of pages
         */
        totalPagesNum: PropTypes.number.isRequired,
        /**
         * Number of pages to display at once
         */
        displayedPagesNum: PropTypes.number,
        /**
         * Currently active page
         */
        page: PropTypes.number.isRequired,
        /**
         * Action which happens on page change
         */
        onPageChange: PropTypes.func.isRequired,
    }

    static defaultProps = {
        displayedPagesNum: 10,
    }

    /**
     * Render left and right and offset navigation buttons
     */
    static renderNavButton(icon, disabled, onClick, className) {
        return (
            <button
                className={classnames(
                    'ui-btn',
                    { 'ui-btn--disabled': disabled },
                    'search-pagination__button',
                    className,
                )}
                onClick={onClick}
            >
                {icon}
            </button>
        );
    }

    /**
     * Cut off offset value
     */
    static clampOffset(offset, { totalPagesNum, displayedPagesNum }) {
        return clamp(offset, 0, totalPagesNum - displayedPagesNum);
    }

    /**
     * Calculate page offset value
     */
    static calcPageOffset({ page, displayedPagesNum }) {
        return Math.floor(page - (displayedPagesNum / 2));
    }

    state = {
        offset: Pagination.clampOffset(Pagination.calcPageOffset(this.props), this.props),
    }

    componentWillReceiveProps(props) {
        this.setOffset(Pagination.calcPageOffset(props));
    }

    setOffset(offset) {
        this.setState({
            offset: Pagination.clampOffset(offset, this.props),
        });
    }

    changePage(page) {
        return this.props.onPageChange(page);
    }

    /**
     * Handle click on page button
     *
     * @param {number} page Page number
     * @returns {*}
     */
    handlePageClick(page) {
        if (page === this.props.page) {
            return false;
        }
        return this.changePage(page);
    }

    /**
     * Handle click on prev page button
     */
    handlePrevPageClick = () => {
        if (this.props.page !== 1) {
            this.changePage(this.props.page - 1);
        }
    }
    /**
     * Handle click on next page button
     */
    handleNextPageClick = () => {
        const { page, totalPagesNum } = this.props;

        if (totalPagesNum > page) {
            this.changePage(page + 1);
        }
    }
    /**
     * Handle click on offset left button
     */
    handleLeftOffsetClick = () => {
        this.setOffset(this.state.offset - this.props.displayedPagesNum);
    }
    /**
     * Handle click on offset right button
     */
    handleRightOffsetClick = () => {
        this.setOffset(this.state.offset + this.props.displayedPagesNum);
    }

    /**
     * Render single page
     * @param {number} page Page number
     */
    renderPage(page) {
        const current = this.props.page === page;
        return (
            <button
                key={page}
                className={classnames('ui-btn_paginationItem ui-btn', {
                    'ui-btn_primary': current,
                }, 'search-pagination__button search-pagination__page')}
                onClick={() => this.handlePageClick(page)}
            >
                {page}
            </button>
        );
    }

    /**
     * Render pages row
     */
    renderPages() {
        const { displayedPagesNum, totalPagesNum } = this.props;
        const { offset } = this.state;

        return range(offset, Math.min(offset + displayedPagesNum, totalPagesNum))
            .map(i => this.renderPage(i + 1));
    }

    render() {
        const { totalPagesNum, displayedPagesNum, page } = this.props;
        if (!totalPagesNum) {
            return null;
        }
        const { offset } = this.state;
        return (
            <div
                className={classnames(
                    'ui-btnGroup',
                    'ui-btnGroup_pagination',
                    'rn-pagination',
                    'search-pagination',
                )}
            >
                {Pagination.renderNavButton(
                    <Svg
                        className="search-pagination__icon"
                        size="Medium"
                        iconName="expand-all"
                    />,
                    offset <= 0,
                    this.handleLeftOffsetClick,
                    'search-pagination__offset search-pagination__offset_left',
                )}
                {Pagination.renderNavButton(
                    <Svg className="search-pagination__icon" size="Medium" iconName="left" />,
                    page <= 1,
                    this.handlePrevPageClick,
                )}

                <span className="ui-btn_paginationRange ui-btn search-pagination__range">
                    Page {page} of {totalPagesNum}
                </span>

                {this.renderPages()}

                {Pagination.renderNavButton(
                    <Svg className="search-pagination__icon" size="Medium" iconName="right" />,
                    page >= totalPagesNum,
                    this.handleNextPageClick,
                )}
                {Pagination.renderNavButton(
                    <Svg
                        className="search-pagination__icon"
                        size="Medium"
                        iconName="expand-all"
                    />,
                    offset >= totalPagesNum - displayedPagesNum,
                    this.handleRightOffsetClick,
                    'search-pagination__offset search-pagination__offset_right',
                )}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/js/Common/Pagination.jsx